/**
 * Admin → Email log — read-only view of the email outbox (GET /admin/email-log).
 *
 * The outbox is the single durable record of every email the app queues:
 * notification sends, distribution-list emails, the SharePoint-sync report.
 * Status: pending = not yet sent, sent = delivered, failed = retries exhausted
 * (hover the row to see the last error). Newest first. Bodies are never shown.
 */

import { useState } from 'react'
import { ErrorCard } from '../components/ErrorCard'
import {
  Badge,
  Body1,
  Button,
  Caption1,
  Input,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Title2,
  Tooltip,
  makeStyles,
  tokens,
} from '@fluentui/react-components'
import { Search16Regular } from '@fluentui/react-icons'
import type { EmailLogRow } from '../types/admin'
import { useEmailLog, useResendEmail, useServiceAccountStatus } from '../hooks/useAdminUsers'
import api from '../lib/api'
import { apiErrorMessage } from '../lib/apiError'
import { useToast } from '../components/ToastProvider'

const STATUS_COLOR: Record<string, 'success' | 'warning' | 'danger' | 'informative'> = {
  sent: 'success',
  pending: 'warning',
  failed: 'danger',
}

const STATUS_FILTERS: { key: string; label: string }[] = [
  { key: '', label: 'All' },
  { key: 'pending', label: 'Pending' },
  { key: 'sent', label: 'Sent' },
  { key: 'failed', label: 'Failed' },
]

const useStyles = makeStyles({
  page: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL },
  controls: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    flexWrap: 'wrap',
  },
  filterRow: { display: 'flex', gap: tokens.spacingHorizontalXS },
  search: { minWidth: '240px' },
  tableWrap: {
    background: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    overflow: 'hidden',
  },
  subject: { maxWidth: '320px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  meta: { color: tokens.colorNeutralForeground3, fontSize: '12px' },
  empty: { padding: '48px 16px', textAlign: 'center', color: tokens.colorNeutralForeground3 },
  svcPanel: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    flexWrap: 'wrap',
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalL}`,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    background: tokens.colorNeutralBackground2,
  },
})

export function AdminEmailLog() {
  const styles = useStyles()
  const [status, setStatus] = useState('')
  const [recipientInput, setRecipientInput] = useState('')
  const [recipient, setRecipient] = useState('')
  const { data, isLoading, error, refetch } = useEmailLog({ status, recipient, limit: 300 })
  const resendEmail = useResendEmail()
  const toast = useToast()
  const svc = useServiceAccountStatus()
  const [connecting, setConnecting] = useState(false)
  const [connectError, setConnectError] = useState<string | null>(null)
  const [teamsTesting, setTeamsTesting] = useState(false)
  const [teamsResult, setTeamsResult] = useState<string | null>(null)
  const [emailRecipients, setEmailRecipients] = useState('')
  const [emailTesting, setEmailTesting] = useState(false)
  const [emailResult, setEmailResult] = useState<string | null>(null)
  const justConnected =
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('svcacct') === 'success'

  const rows: EmailLogRow[] = data?.items ?? []
  const counts = data?.status_counts ?? {}

  function applySearch() {
    setRecipient(recipientInput.trim())
  }

  async function connectServiceAccount() {
    setConnectError(null)
    setConnecting(true)
    try {
      const res = await api.get('/admin/service-account/auth')
      window.location.href = res.data.auth_url // off to the Microsoft sign-in
    } catch (e) {
      setConnectError(apiErrorMessage(e, 'Could not start the connection flow.'))
      setConnecting(false)
    }
  }

  async function testTeamsDm() {
    setTeamsResult(null)
    setTeamsTesting(true)
    try {
      const res = await api.post('/admin/test-teams-dm')
      const d = res.data as {
        configured?: boolean
        sent?: boolean
        status?: number
        detail?: string | null
      }
      if (d.sent) {
        setTeamsResult('✓ Sent — check your Teams for a Flow-bot DM.')
      } else if (!d.configured) {
        setTeamsResult('Teams is not configured on this server (TEAMS_WEBHOOK_URL / AZURE_AD_* missing).')
      } else {
        setTeamsResult(`Not sent${d.status ? ` (HTTP ${d.status})` : ''}: ${d.detail || 'unknown error'}`)
      }
    } catch (e) {
      setTeamsResult(apiErrorMessage(e, 'Could not run the Teams test.'))
    } finally {
      setTeamsTesting(false)
    }
  }

  async function testEmail() {
    setEmailResult(null)
    setEmailTesting(true)
    try {
      const res = await api.post('/admin/test-email', null, {
        params: emailRecipients.trim() ? { recipients: emailRecipients.trim() } : {},
      })
      const d = res.data as {
        transport?: string
        mail_disabled?: boolean
        results?: { recipient: string; ok: boolean; error?: string }[]
      }
      const parts = (d.results ?? []).map((r) =>
        r.ok ? `✓ ${r.recipient}` : `✗ ${r.recipient}: ${r.error || 'failed'}`,
      )
      const disabled = d.mail_disabled ? ' — MAIL_DISABLED (suppressed, not actually sent)' : ''
      setEmailResult(`via ${d.transport ?? '?'}${disabled} — ${parts.join('   ·   ')}`)
    } catch (e) {
      setEmailResult(apiErrorMessage(e, 'Could not run the email test.'))
    } finally {
      setEmailTesting(false)
    }
  }

  const diagnostics = (
    <details open={justConnected || !!connectError || undefined}>
      <summary>Delivery diagnostics</summary>
      <div className={styles.svcPanel} data-testid="svc-account-panel">
        <div>
          <Body1 style={{ fontWeight: 600 }}>Email sending account</Body1>
          <Caption1 block style={{ color: tokens.colorNeutralForeground2 }}>
            Notifications are sent as{' '}
            <strong>{svc.data?.account ?? 'RDDAC@bayer.com'}</strong>.{' '}
            {justConnected ? (
              <span style={{ color: tokens.colorPaletteGreenForeground1 }}>
                ✓ Connected — sends resume within ~20 seconds.
              </span>
            ) : svc.data?.connected ? (
              <span style={{ color: tokens.colorPaletteGreenForeground1 }}>Connected.</span>
            ) : (
              <span style={{ color: tokens.colorPaletteRedForeground1, fontWeight: 600 }}>
                Not connected — emails are not being sent.
              </span>
            )}
          </Caption1>
          {connectError && (
            <Caption1 block style={{ color: tokens.colorPaletteRedForeground1 }}>
              {connectError}
            </Caption1>
          )}
        </div>
        <Button
          appearance={svc.data?.connected || justConnected ? 'secondary' : 'primary'}
          onClick={connectServiceAccount}
          disabled={connecting}
          data-testid="svc-account-connect"
        >
          {connecting
            ? 'Opening…'
            : svc.data?.connected || justConnected
              ? 'Reconnect'
              : 'Connect email account'}
        </Button>
      </div>

      <div className={styles.svcPanel} data-testid="teams-test-panel">
        <div>
          <Body1 style={{ fontWeight: 600 }}>Teams notifications</Body1>
          <Caption1 block style={{ color: tokens.colorNeutralForeground2 }}>
            Test delivery to your Teams account.
          </Caption1>
          {teamsResult && (
            <Caption1 block style={{ color: tokens.colorNeutralForeground1 }}>
              {teamsResult}
            </Caption1>
          )}
        </div>
        <Button
          appearance="secondary"
          onClick={testTeamsDm}
          disabled={teamsTesting}
          data-testid="teams-test-dm"
        >
          {teamsTesting ? 'Sending…' : 'Send test Teams DM'}
        </Button>
      </div>

      <div className={styles.svcPanel} data-testid="email-test-panel">
        <div style={{ flex: 1, minWidth: '240px' }}>
          <Body1 style={{ fontWeight: 600 }}>Email delivery</Body1>
          <Caption1 block style={{ color: tokens.colorNeutralForeground2 }}>
            Leave blank to send to yourself. Separate other addresses with commas.
          </Caption1>
          {emailResult && (
            <Caption1 block style={{ color: tokens.colorNeutralForeground1 }}>
              {emailResult}
            </Caption1>
          )}
        </div>
        <Input
          value={emailRecipients}
          onChange={(_e, d) => setEmailRecipients(d.value)}
          placeholder="you@bayer.com, other@bayer.com"
          style={{ minWidth: '240px' }}
          data-testid="email-test-recipients"
        />
        <Button
          appearance="secondary"
          onClick={testEmail}
          disabled={emailTesting}
          data-testid="email-test-send"
        >
          {emailTesting ? 'Sending…' : 'Send test email'}
        </Button>
      </div>
    </details>
  )

  return (
    <div className={styles.page}>
      <Title2 block>Email log</Title2>
      <div className={styles.controls}>
        <div className={styles.filterRow}>
          {STATUS_FILTERS.map((f) => {
            const n = f.key ? counts[f.key] : Object.values(counts).reduce((a, b) => a + b, 0)
            return (
              <Button
                key={f.key || 'all'}
                size="small"
                appearance={status === f.key ? 'primary' : 'subtle'}
                onClick={() => setStatus(f.key)}
                data-testid={`email-log-filter-${f.key || 'all'}`}
              >
                {f.label}
                {n != null ? ` (${n})` : ''}
              </Button>
            )
          })}
        </div>
        <Input
          className={styles.search}
          placeholder="Filter by recipient…"
          value={recipientInput}
          onChange={(_, d) => setRecipientInput(d.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') applySearch()
          }}
          contentBefore={<Search16Regular />}
          data-testid="email-log-recipient"
        />
        <Button size="small" onClick={applySearch}>
          Search
        </Button>
      </div>

      {isLoading ? (
        <div className={styles.empty}>
          <Spinner size="small" />
        </div>
      ) : error ? (
        <div data-testid="email-log-error">
          <ErrorCard
            title="Couldn't load the email log"
            message={apiErrorMessage(error, 'Please try again.')}
            retry={() => {
              void refetch()
            }}
          />
        </div>
      ) : rows.length === 0 ? (
        <div className={styles.empty}>No emails match.</div>
      ) : (
        <div className={styles.tableWrap}>
          <Table aria-label="Email log" size="small">
            <TableHeader>
              <TableRow>
                <TableHeaderCell style={{ width: '15%' }}>Queued</TableHeaderCell>
                <TableHeaderCell style={{ width: '9%' }}>Status</TableHeaderCell>
                <TableHeaderCell style={{ width: '22%' }}>Recipient</TableHeaderCell>
                <TableHeaderCell style={{ width: '32%' }}>Subject</TableHeaderCell>
                <TableHeaderCell style={{ width: '16%' }}>Event</TableHeaderCell>
                <TableHeaderCell style={{ width: '6%' }}>Tries</TableHeaderCell>
                <TableHeaderCell style={{ width: '8%' }}>Resend</TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id}>
                  <TableCell>
                    <Caption1>{r.created_at ? new Date(r.created_at).toLocaleString() : '—'}</Caption1>
                  </TableCell>
                  <TableCell>
                    {r.status === 'failed' && r.last_error ? (
                      <Tooltip content={r.last_error} relationship="description">
                        <Badge appearance="tint" color="danger">
                          failed
                        </Badge>
                      </Tooltip>
                    ) : (
                      <Badge appearance="tint" color={STATUS_COLOR[r.status] || 'informative'}>
                        {r.status}
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>{r.recipient}</TableCell>
                  <TableCell>
                    <span className={styles.subject} title={r.subject}>
                      {r.subject}
                    </span>
                  </TableCell>
                  <TableCell>
                    {r.event ? (
                      <Caption1>{r.event}</Caption1>
                    ) : (
                      <span className={styles.meta}>—</span>
                    )}
                  </TableCell>
                  <TableCell>{r.attempts}</TableCell>
                  <TableCell>
                    {/* Re-queues the STORED body, so this delivers the message
                        that was actually composed — not a fresh render of a
                        demand that has changed since. Disabled while pending
                        because the row is already on its way out. */}
                    <Button
                      size="small"
                      appearance="subtle"
                      disabled={r.status === 'pending' || resendEmail.isPending}
                      title={
                        r.status === 'pending'
                          ? 'Already queued — it has not been sent yet'
                          : 'Send this exact email again'
                      }
                      onClick={() => {
                        resendEmail.mutate(r.id, {
                          onSuccess: (d) =>
                            toast.showToast({
                              title: d.requeued
                                ? 'Queued to send again'
                                : d.detail || 'Already queued',
                              intent: d.requeued ? 'success' : 'warning',
                            }),
                          onError: (err) =>
                            toast.showToast({
                              title: apiErrorMessage(err, 'Could not re-queue that email'),
                              intent: 'error',
                            }),
                        })
                      }}
                      data-testid={`resend-${r.id}`}
                    >
                      Resend
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      {diagnostics}
    </div>
  )
}
